import { createReducer, createActions } from 'reduxsauce';
import moment from 'moment';
import union from 'lodash-es/union';
import get from 'lodash-es/get';
import {
  getCurrentMonth,
  getCurrentYear,
  getWeekRange,
  getStartDateOfWeek,
  getFirstWeekOfMonth
} from '@utils/date';
import {
  TIME_FRAME,
  ADDONS,
  SERVICE_TYPES,
  TIME_FRAME_TITLES
} from '@services/minte';

import { AppSelectors } from './AppRedux';
import { BookingSelectors } from './BookingRedux';

export const SCHEDULE_PACKAGES = {
  MIX_MATCH: 'mix_match',
  CLASSIC: 'classic',
  PRESTIGE: 'prestige',
  SIGNATURE: 'signature'
};

const { Types, Creators } = createActions({
  resetRecurringBooking: null,
  initRecurringBookingFromSubscription: ['activeSubscription'],
  addSelectedItem: ['item'],
  removeSelectedItem: ['item'],
  updateSelectedItem: ['item'],
  addRemoveSelectedItem: ['item', 'newItem'],
  attachRescheduleInfoToItem: ['item', 'info'],
  attachSkipInfoToItem: ['item'],
  setScheduleItems: ['items'],
  setScheduleItemsAtOnce: ['rows', 'col', 'serviceType', 'addons'],
  attachAddonToItem: ['item'],
  detachAddonToItem: ['item'],
  moveAddonToItem: ['item', 'newItem'],
  addScheduleBoardRow: null,
  removeScheduleBoardRow: null,
  setAllPackages: ['allPackages'],
  setCurrentPackage: ['packageType'],
  setRecurringStartDate: ['startDate'],
  setRecurringCalendarMonth: ['month'],
  setRecurringCalendarYear: ['year'],
  setRecurringAvailableDates: ['availableDates'],
  setRecurringBlockedDays: ['blockedDays'],
  setRecurringRescheduleInfo: ['info'],
  setRecurringConfirmStartDate: ['availableDates']
});

export const ScheduleTypes = Types;
export default Creators;

/* ------- Initial State --------- */

const DEFAULT_PACKAGES = {
  [SCHEDULE_PACKAGES.MIX_MATCH]: {
    title: 'Mix & Match',
    icons: ['hotel-style', 'deep-clean']
  }
};
export const INITIAL_STATE = {
  selectedItems: [],
  numOfRows: 4,
  disabledItems: [],
  serviceTypes: {},
  allPackages: {
    ...DEFAULT_PACKAGES
  },
  currentPackage: SCHEDULE_PACKAGES.MIX_MATCH,
  currentMonth: getCurrentMonth(),
  currentYear: getCurrentYear(),
  startDate: null,
  availableDates: [],
  blockedDays: [],
  rescheduleInfo: null,
  validReschedule: false,
  isScheduleChecking: false
};

const scheduleItemSorter = (a, b) => {
  if (a.row === b.row) {
    return a.col - b.col;
  }
  return a.row - b.row;
};

/* ------- Selectors --------- */
export const ScheduleSelectors = {
  selectNumOfRows: state => state.schedule.numOfRows,
  selectNumOfServiceType: (state, serviceType) => {
    if (state.schedule.currentPackage === SCHEDULE_PACKAGES.MIX_MATCH) {
      return -1;
    }

    return state.schedule.serviceTypes[serviceType] || 0;
  },
  selectPackage: state => state.schedule.currentPackage,
  selectScheduledItems: state => state.schedule.selectedItems,
  selectAllPackages: state => {
    return state.schedule.allPackages;
  },
  selectScheduledPrice: state => {
    const items = state.schedule.selectedItems;
    const propertyInfo = AppSelectors.selectPropertyInfoOf(
      state,
      BookingSelectors.selectCurrentProperty(state)
    );
    return items.reduce((acc, item) => {
      const { serviceType } = item;
      const price = get(propertyInfo, `serviceTypes.${serviceType}`, 0);
      return acc + price;
    }, 0);
  },
  selectCurrentMonth: state => state.schedule.currentMonth,
  selectCurrentYear: state => state.schedule.currentYear,
  selectWeekRange: state =>
    getWeekRange(
      state.schedule.startDate,
      state.schedule.numOfRows,
      state.schedule.selectedItems
    ),
  selectStartDate: state => state.schedule.startDate,
  selectFirstServiceDate: state => {
    if (!state.schedule.selectedItems || !state.schedule.selectedItems.length) {
      return null;
    }
    const toSort = [...state.schedule.selectedItems];
    toSort.sort(scheduleItemSorter);
    const firstItem = toSort[0];
    const { serviceDate } = firstItem;
    const { momentDate } = serviceDate || {};
    return momentDate;
  },
  selectAvailableDates: state => state.schedule.availableDates,
  selectBlockedDays: state => state.schedule.blockedDays,
  selectRescheduleInfo: state => state.schedule.rescheduleInfo,
  selectIsValidReschedule: state => state.schedule.validReschedule
};

/* -------- Reducers ---------- */

export const setRecurringStartDate = (state, { startDate }) => {
  const { numOfRows, selectedItems, currentMonth } = state;
  const weekRange = getWeekRange(startDate, numOfRows, selectedItems);
  const { start: weekStart, year: startYear } = weekRange;
  const weekStartDate = getStartDateOfWeek(weekStart, startYear);

  const firstOfNextMonth = getFirstWeekOfMonth(currentMonth + 1);

  let month = currentMonth;

  if (weekStart === firstOfNextMonth) {
    month = month === 12 ? 1 : month + 1;
  }

  return {
    ...state,
    startDate,
    selectedItems: selectedItems.map(item => {
      const { row, col, serviceDate } = item;
      const momentDate = weekStartDate
        .clone()
        .add(+row, 'week')
        .add(+col + 1, 'day');

      let { timeframe } = serviceDate || {};

      if (!timeframe) {
        timeframe = item.timeframe;
      }

      if (!timeframe) {
        timeframe = TIME_FRAME.ANYTIME;
      }

      return {
        ...item,
        serviceDate: {
          momentDate,
          formattedDate: momentDate.format('MMM DD'),
          timestamp: momentDate.unix(),
          timeframeTitle: TIME_FRAME_TITLES[timeframe],
          timeframe,
          availableWindow: [
            TIME_FRAME.ANYTIME,
            TIME_FRAME.MORNING,
            TIME_FRAME.AFTERNOON
          ]
        }
      };
    }),
    currentMonth: month,
    currentYear: startYear
  };
};

export const addSelectedItem = (state, { item }) => {
  const { serviceType } = item;
  const { serviceTypes, selectedItems, startDate } = state;

  let newState;
  if (serviceTypes[serviceType] !== undefined) {
    newState = {
      ...state,
      serviceTypes: {
        ...serviceTypes,
        [serviceType]: serviceTypes[serviceType] - 1
      },
      selectedItems: selectedItems.concat(item)
    };
  } else {
    newState = {
      ...state,
      selectedItems: selectedItems.concat(item)
    };
  }

  if (startDate) {
    return setRecurringStartDate(newState, { startDate });
  }

  return newState;
};

export const removeSelectedItem = (state, { item }) => {
  const { serviceType } = item;
  const { serviceTypes, selectedItems, startDate } = state;
  let newState;
  if (serviceTypes[serviceType] !== undefined) {
    newState = {
      ...state,
      serviceTypes: {
        ...serviceTypes,
        [serviceType]: serviceTypes[serviceType] + 1
      },
      selectedItems: selectedItems.filter(
        existing => existing.row !== item.row || existing.col !== item.col
      )
    };
  } else {
    newState = {
      ...state,
      selectedItems: selectedItems.filter(
        existing => existing.row !== item.row || existing.col !== item.col
      )
    };
  }

  if (startDate) {
    return setRecurringStartDate(newState, { startDate });
  }

  return newState;
};

export const updateSelectedItem = (state, { item }) => {
  const { selectedItems, startDate } = state;
  const { row, col } = item;
  let newState = {
    ...state,
    selectedItems: selectedItems.map(existing =>
      existing.row !== row || existing.col !== col ? existing : item
    )
  };

  if (startDate) {
    return setRecurringStartDate(newState, { startDate });
  }

  return newState;
};

export const moveSelectedItem = (state, { item, newItem }) => {
  const { selectedItems, startDate } = state;
  const newSelectedItems = selectedItems
    .filter(existing => existing.row !== item.row || existing.col !== item.col)
    .concat(newItem);

  let newState = {
    ...state,
    selectedItems: newSelectedItems
  };

  if (startDate) {
    return setRecurringStartDate(newState, { startDate });
  }

  return newState;
};

export const addRow = state => {
  return {
    ...state,
    numOfRows: state.numOfRows + 1
  };
};

export const removeRow = state => {
  const { numOfRows, selectedItems, serviceTypes } = state;

  const newItems = [];
  const newServiceTypes = { ...serviceTypes };
  selectedItems.forEach(item => {
    if (+item.row === numOfRows - 1) {
      const { serviceType } = item;
      if (newServiceTypes[serviceType] !== undefined) {
        newServiceTypes[serviceType] += 1;
      }
      return;
    }
    newItems.push(item);
  });

  return {
    ...state,
    selectedItems: newItems,
    serviceTypes: newServiceTypes,
    numOfRows: state.numOfRows - 1
  };
};

export const setAllPackages = (state, { allPackages = DEFAULT_PACKAGES }) => {
  return {
    ...state,
    allPackages
  };
};
export const setCurrentPackage = (state, { packageType }) => {
  return {
    ...state,
    currentPackage: packageType,
    serviceTypes: {
      ...state.allPackages[packageType]
    },
    selectedItems: []
  };
};

export const attachAddonToItem = (state, { item }) => {
  const { selectedItems, startDate } = state;
  const { addon, row, col } = item;
  let newState = {
    ...state,
    selectedItems: selectedItems.map(existing =>
      (existing.serviceType === SERVICE_TYPES.DEEP_CLEAN &&
        addon == ADDONS.MOPPING) ||
      existing.row !== row ||
      existing.col !== col
        ? existing
        : {
            ...existing,
            addons: union(existing.addons, [addon])
          }
    )
  };

  if (startDate) {
    return setRecurringStartDate(newState, { startDate });
  }

  return newState;
};

export const detachAddonToItem = (state, { item }) => {
  const { selectedItems, startDate } = state;
  const { addon, row, col } = item;
  let newState = {
    ...state,
    selectedItems: selectedItems.map(existing =>
      existing.row !== row || existing.col !== col
        ? existing
        : {
            ...existing,
            addons: existing.addons.filter(attached => attached !== addon)
          }
    )
  };

  if (startDate) {
    return setRecurringStartDate(newState, { startDate });
  }

  return newState;
};

export const moveAddonToItem = (state, { item, newItem }) => {
  const { selectedItems, startDate } = state;
  const { addon, row, col } = item;
  const { addon: newAddon, row: newRow, col: newCol } = newItem;

  const source = selectedItems.find(
    item => item.row === row && item.col === col
  );

  if (!source || !source.addons.includes(addon)) {
    return state;
  }

  const target = selectedItems.find(
    item => item.row === newRow && item.col === newCol
  );
  if (!target || (target.addons && target.addons.includes(newAddon))) {
    return state;
  }

  if (
    target.serviceType === SERVICE_TYPES.DEEP_CLEAN &&
    newAddon == ADDONS.MOPPING
  ) {
    return state;
  }

  let newState = {
    ...state,
    selectedItems: selectedItems.map(item => {
      if (item.row === row && item.col === col) {
        return {
          ...item,
          addons: item.addons.filter(attached => attached !== addon)
        };
      } else if (item.row === newRow && item.col === newCol) {
        return {
          ...item,
          addons: union(item.addons, [newAddon])
        };
      }
      return item;
    })
  };

  if (startDate) {
    return setRecurringStartDate(newState, { startDate });
  }

  return newState;
};

export const setRecurringCalendarMonth = (state, { month }) => {
  return {
    ...state,
    currentMonth: month
  };
};

export const setRecurringCalendarYear = (state, { year }) => {
  return {
    ...state,
    currentYear: year
  };
};

export const setScheduleItemsAtOnce = (
  state,
  { rows, col, serviceType, addons }
) => {
  return {
    ...state,
    selectedItems: rows.map(row => ({
      row,
      col,
      serviceType,
      addons
    }))
  };
};

export const setScheduleItems = (state, { items }) => {
  return {
    ...state,
    selectedItems: items
  };
};

export const resetSchedules = state => {
  const { allPackages, blockedDays } = state;
  return {
    ...INITIAL_STATE,
    allPackages,
    blockedDays
  };
};

export const setAvailableDates = (state, { availableDates }) => {
  return {
    ...state,
    availableDates
  };
};

export const setBlockedDays = (state, { blockedDays }) => {
  return {
    ...state,
    blockedDays
  };
};

export const setRescheduleInfo = (state, { info }) => {
  return {
    ...state,
    rescheduleInfo: info
  };
};

export const attachRescheduleInfoToItem = (state, { item, info }) => {
  let { numOfRows, selectedItems, startDate } = state;

  if (!startDate) {
    startDate = moment();
  }

  const weekRange = getWeekRange(startDate, numOfRows, selectedItems);
  const { start: weekStart, year: startYear } = weekRange;
  const weekStartDate = getStartDateOfWeek(weekStart, startYear);

  const { row: newRow, col: newCol } = info;
  const momentDate = weekStartDate
    .clone()
    .add(+newRow, 'week')
    .add(+newCol + 1, 'day');

  const rescheduleDate = {
    momentDate,
    formattedDate: momentDate.format('MMM DD'),
    timestamp: momentDate.unix()
  };

  const { row, col } = item;

  return {
    ...state,
    validReschedule: true,
    selectedItems: selectedItems.map(existing =>
      +existing.row !== +row || +existing.col !== +col
        ? existing
        : {
            ...existing,
            rescheduleItem: { ...info, rescheduleDate }
          }
    )
  };
};

export const attachSkipInfoToItem = (state, { item }) => {
  const { selectedItems } = state;
  const { row, col } = item;

  return {
    ...state,
    validReschedule: true,
    selectedItems: selectedItems.map(existing =>
      +existing.row !== +row || +existing.col !== +col
        ? existing
        : {
            ...existing,
            rescheduleItem: { skip: true }
          }
    )
  };
};

/* -------- Hookup Reducers to Types -------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.RESET_RECURRING_BOOKING]: resetSchedules,
  [Types.SET_SCHEDULE_ITEMS]: setScheduleItems,
  [Types.SET_SCHEDULE_ITEMS_AT_ONCE]: setScheduleItemsAtOnce,
  [Types.ADD_SELECTED_ITEM]: addSelectedItem,
  [Types.REMOVE_SELECTED_ITEM]: removeSelectedItem,
  [Types.UPDATE_SELECTED_ITEM]: updateSelectedItem,
  [Types.ADD_REMOVE_SELECTED_ITEM]: moveSelectedItem,
  [Types.ADD_SCHEDULE_BOARD_ROW]: addRow,
  [Types.REMOVE_SCHEDULE_BOARD_ROW]: removeRow,
  [Types.SET_ALL_PACKAGES]: setAllPackages,
  [Types.SET_CURRENT_PACKAGE]: setCurrentPackage,
  [Types.DETACH_ADDON_TO_ITEM]: detachAddonToItem,
  [Types.ATTACH_ADDON_TO_ITEM]: attachAddonToItem,
  [Types.MOVE_ADDON_TO_ITEM]: moveAddonToItem,
  [Types.SET_RECURRING_START_DATE]: setRecurringStartDate,
  [Types.SET_RECURRING_CALENDAR_MONTH]: setRecurringCalendarMonth,
  [Types.SET_RECURRING_CALENDAR_YEAR]: setRecurringCalendarYear,
  [Types.SET_RECURRING_AVAILABLE_DATES]: setAvailableDates,
  [Types.SET_RECURRING_BLOCKED_DAYS]: setBlockedDays,
  [Types.SET_RECURRING_RESCHEDULE_INFO]: setRescheduleInfo,
  [Types.ATTACH_RESCHEDULE_INFO_TO_ITEM]: attachRescheduleInfoToItem,
  [Types.ATTACH_SKIP_INFO_TO_ITEM]: attachSkipInfoToItem
});
